import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiEmail } from '@mdi/js';

import { Checkbox } from 'common/components/Checkbox';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { FilterBar } from 'common/components/FilterBar';
import { IconButton } from 'common/components/IconButton';
import { BoxLoader } from 'common/components/Loader';
import { Pagination } from 'common/components/Pagination';
import { TableCell, TableHead, useSelection } from 'common/components/Table';
import { TFilterFormValues } from 'common/types';
import { ISkillInputOptionFragment, SkillDocument } from 'graphql/types';
import { useSkillCertificateMatrix } from 'skill/hooks/useSkillCertificateMatrix';
import { useSkillCertificateMatrixFilter } from 'skill/hooks/useSkillCertificateMatrixFilter';
import { SkillCertificateOfferEventDialog } from 'skill/components/SkillCertificateOfferEventDialog';
import { ESkillCertificateOfferEventDialogMode } from 'skill/types';
import { UserEmailDialog } from 'user/components/UserEmailDialog';

import { SkillCertificateMatrixRow } from './SkillCertificateMatrixRow';

interface IProps {
    groupId: string;
    skillId?: string;
    skip?: boolean;
}

export const SkillCertificateMatrix = ({ groupId, skillId, skip }: IProps) => {
    const [translate] = useTranslation();

    const [userEmailDialogOpen, setUserEmailDialogOpen] = useState(false);
    const [eventDialogMode, setEventDialogMode] =
        useState<ESkillCertificateOfferEventDialogMode | null>(null);

    // Use no-cache to prevent writing groupRequirements.group back to the
    // Group cache, which would trigger a re-render in the parent GroupForm.
    const { data: skillData, loading: skillLoading } = useQuery(
        SkillDocument,
        !skip && !!skillId
            ? { variables: { id: skillId }, fetchPolicy: 'no-cache' }
            : skipToken
    );

    const skillCertificates = useMemo(
        () =>
            (skillData?.skill?.skillCertificates || [])
                .filter((sc) => !!sc?.certificate)
                .map((sc) => sc!),
        [skillData]
    );

    const skillForDialog: ISkillInputOptionFragment | null = useMemo(() => {
        if (!skillData?.skill) return null;

        return {
            __typename: 'SkillListItem',
            id: skillData.skill.id,
            name: skillData.skill.name,
        };
    }, [skillData]);

    const hasMandatoryCertificates = useMemo(
        () => skillCertificates.some((sc) => sc.isMandatory),
        [skillCertificates]
    );

    const hasLoadedSkill = !skillLoading && !!skillData?.skill;
    const hasNoCertificates = hasLoadedSkill && !skillCertificates.length;

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        searchQuery,
        onSearch,
        onSearchClear,
        onFilterChange,
    } = useSkillCertificateMatrixFilter({ hasMandatoryCertificates });

    const {
        users,
        reportItems,
        loading: dataLoading,
        paginationSettings,
        setPage,
    } = useSkillCertificateMatrix({
        groupId,
        skillId,
        searchQuery,
        skip: skip || skillLoading || hasNoCertificates,
        ...filterValuesForQuery,
    });

    const loading = dataLoading || skillLoading;

    const { rows: userRows, selected, onSelectAll } = useSelection(users);

    const isAllSelected = useMemo(
        () => !!users.length && selected.length === users.length,
        [selected, users]
    );

    const hasSelection = selected.length > 0;

    const handleSearch = useCallback(
        (value: string) => {
            setPage(1);
            onSearch(value);
        },
        [setPage, onSearch]
    );

    const handleSearchClear = useCallback(() => {
        setPage(1);
        onSearchClear();
    }, [setPage, onSearchClear]);

    const handleFilterChange = useCallback(
        (values?: TFilterFormValues) => {
            setPage(1);
            onFilterChange(values);
        },
        [setPage, onFilterChange]
    );

    const tableActions = (
        <Stack direction="row" spacing={0.5}>
            <DropdownMenu
                anchor={
                    <IconButton
                        aria-label={translate('skillsMatrix.mailButtonText')}
                        color="inherit"
                        iconPath={mdiEmail}
                        iconSize="2.4rem"
                    />
                }
                placement="bottom-start"
            >
                <MenuItem
                    onClick={() =>
                        setEventDialogMode(
                            ESkillCertificateOfferEventDialogMode.Enrollment
                        )
                    }
                >
                    {translate('skillsMatrix.enrollInOffer')}
                </MenuItem>

                <MenuItem
                    onClick={() =>
                        setEventDialogMode(
                            ESkillCertificateOfferEventDialogMode.Notify
                        )
                    }
                >
                    {translate('skillsMatrix.notifyAboutOffer')}
                </MenuItem>

                <MenuItem onClick={() => setUserEmailDialogOpen(true)}>
                    {translate('skillsMatrix.personalMessage')}
                </MenuItem>
            </DropdownMenu>
        </Stack>
    );

    return (
        <>
            {!hasNoCertificates && (
                <FilterBar
                    useFilterDrawer
                    formFilters={filters}
                    formFilterValues={filterValues}
                    initialSearchValue={searchQuery}
                    placeholder={translate('filterBarPlaceholder')}
                    onFiltersChange={handleFilterChange}
                    onSearch={handleSearch}
                    onSearchClear={handleSearchClear}
                />
            )}

            {loading && <BoxLoader />}

            {!loading && hasNoCertificates && (
                <Typography>
                    {translate('groupSkillDrawer.noCertificates')}
                </Typography>
            )}

            {!loading && !!skillCertificates.length && !users.length && (
                <Box sx={{ mt: 8, textAlign: 'center' }}>
                    <Typography>
                        {translate('groupSkillDrawer.noResults')}
                    </Typography>
                </Box>
            )}

            {!loading && !!users.length && !!skillCertificates.length && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ verticalAlign: 'bottom' }}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={isAllSelected}
                                        indeterminate={
                                            !isAllSelected && hasSelection
                                        }
                                        onChange={onSelectAll}
                                    />
                                </TableCell>

                                <TableCell sx={{ verticalAlign: 'bottom' }}>
                                    {!hasSelection && (
                                        <Box sx={{ pb: '3px' }}>
                                            {translate('participant')}
                                        </Box>
                                    )}

                                    {hasSelection && (
                                        <Stack
                                            useFlexGap
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                alignItems: 'center',
                                                pb: '1px',
                                            }}
                                        >
                                            <Typography
                                                fontWeight={700}
                                                variant="body2"
                                            >
                                                {translate('selectedCount', {
                                                    value: selected.length,
                                                })}
                                            </Typography>

                                            <Divider
                                                flexItem
                                                orientation="vertical"
                                                sx={{ ml: 1 }}
                                            />

                                            <Box sx={{ height: '100%' }}>
                                                {tableActions}
                                            </Box>
                                        </Stack>
                                    )}
                                </TableCell>

                                {skillCertificates.map((sc) => (
                                    <TableCell
                                        vertical
                                        key={sc.certificate?.id}
                                    >
                                        <Tooltip
                                            title={sc.certificate?.name || ''}
                                        >
                                            <Box>{sc.certificate?.name}</Box>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user, index) => {
                                const portfolioItem = reportItems.find(
                                    (item) => item.user?.id === user.id
                                );
                                const row = userRows[index];

                                return (
                                    <SkillCertificateMatrixRow
                                        certificates={skillCertificates}
                                        isSelected={row?.isSelected}
                                        key={user.id}
                                        portfolioItem={portfolioItem}
                                        portfolioItemChildren={
                                            portfolioItem?.children ?? undefined
                                        }
                                        skill={skillForDialog}
                                        user={user}
                                        onSelect={row?.onSelect}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading &&
                !!paginationSettings &&
                !!users.length &&
                !!skillCertificates.length && (
                    <Box sx={{ mt: 2 }}>
                        <Pagination
                            page={paginationSettings.page}
                            pageAmount={paginationSettings.pageAmount}
                            totalsAmount={paginationSettings.count}
                            totalsText={translate(
                                'groupSkillDrawer.paginationParticipants',
                                { count: paginationSettings.count }
                            )}
                            onChange={(page: number) => {
                                setPage(page);
                            }}
                        />
                    </Box>
                )}

            <UserEmailDialog
                open={userEmailDialogOpen}
                userIds={selected}
                onClose={() => setUserEmailDialogOpen(false)}
                onComplete={() => {
                    setUserEmailDialogOpen(false);
                    onSelectAll();
                }}
            />

            <SkillCertificateOfferEventDialog
                mode={eventDialogMode ?? undefined}
                open={!!eventDialogMode}
                skill={skillForDialog}
                userIds={selected}
                onClose={() => setEventDialogMode(null)}
                onComplete={onSelectAll}
            />
        </>
    );
};
